/**
 * ProcessingStepList
 *
 * Shared component for rendering a list of processing steps with
 * status icons (check/spinner/error/pending). Used by both
 * UploadProcessingView and DocumentDetailContent.
 */

import { CheckCircle2, Loader2, XCircle, Circle } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "@/packages/utils";

export type ProcessingStepStatus =
  | "pending"
  | "generating"
  | "completed"
  | "failed"
  | "retrying";

export interface ProcessingStep {
  label: string;
  status: ProcessingStepStatus;
  /** Optional description shown while step is actively processing */
  activeDescription?: string;
}

function StepIcon({ status }: { status: ProcessingStepStatus }) {
  return (
    <AnimatePresence mode="wait">
      {status === "completed" && (
        <motion.div
          key="completed"
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: "spring", stiffness: 400, damping: 15 }}
          className="shrink-0"
        >
          <CheckCircle2 size={14} className="text-emerald-500" />
        </motion.div>
      )}
      {(status === "generating" || status === "retrying") && (
        <motion.div
          key="loading"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="shrink-0"
        >
          <Loader2 size={14} className="text-blue-500 animate-spin" />
        </motion.div>
      )}
      {status === "failed" && (
        <motion.div
          key="failed"
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="shrink-0"
        >
          <XCircle size={14} className="text-red-500" />
        </motion.div>
      )}
      {status === "pending" && (
        <motion.div
          key="pending"
          initial={{ opacity: 0.5 }}
          animate={{ opacity: 1 }}
          className="shrink-0"
        >
          <Circle size={14} className="text-muted" />
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function StepStatusLabel({ status }: { status: ProcessingStepStatus }) {
  const { t } = useTranslation("documents");

  if (status === "completed") return null;
  if (status === "generating") return t("processing.status.inProgress");
  if (status === "retrying") return t("processing.status.retrying");
  if (status === "failed") return t("processing.status.failed");
  return t("processing.status.waiting");
}

export function ProcessingStepList({ steps }: { steps: ProcessingStep[] }) {
  return (
    <div className="space-y-1.5">
      {steps.map((step) => (
        <div key={step.label} className="flex flex-col gap-0.5">
          <div className="flex items-center gap-2">
            <StepIcon status={step.status} />
            <span
              className={`text-xs ${
                step.status === "completed"
                  ? "text-secondary line-through"
                  : step.status === "failed"
                    ? "text-red-500"
                    : "text-primary"
              }`}
            >
              {step.label}
            </span>
            {step.status !== "completed" && (
              <span className="text-xs text-muted ml-auto">
                <StepStatusLabel status={step.status} />
              </span>
            )}
          </div>
          {step.activeDescription &&
            (step.status === "generating" || step.status === "retrying") && (
              <motion.p
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                className="text-[11px] text-muted ml-5 leading-relaxed"
              >
                {step.activeDescription}
              </motion.p>
            )}
        </div>
      ))}
    </div>
  );
}
